import React, { useState, useCallback, AriaAttributes } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import MoreIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import DotIndicator from '@wix/da-ds/pkg/Indicators/DotIndicator';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import BetaBadge, { BetaBadgeProps } from '@wix/da-ds/pkg/BetaBadge';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import ConditionalTooltip from '../../lego/ConditionalTooltip';
import s from './NetworkBarItem.scss';

export interface Props {
  url?: string;
  icon: React.ReactNode;
  isSelected?: boolean;
  isCollapsed?: boolean;
  hasNewContent?: boolean;
  betaBadge?: BetaBadgeProps['badgeType'];
  tabIndex?: number;
  tooltipContent: React.ReactNode;
  showTooltip?: boolean;
  className?: string;
  linkClassName?: string;
  iconClassName?: string;
  menuClassName?: string;
  biData?: BiEvent;
  menuItems?: NullableDropdownItem[];
  menuBiData?: BiEvent;
  children: React.ReactNode;
  id?: string;
  isTaller?: boolean;
  'aria-label'?: AriaAttributes['aria-label'];
  'aria-controls'?: AriaAttributes['aria-controls'];
  'aria-expanded'?: AriaAttributes['aria-expanded'];
  onClick?: () => void;
}

const NetworkBarItem: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  tooltipContent,
  isCollapsed = false,
  className,
  linkClassName,
  iconClassName,
  isSelected,
  url,
  tabIndex,
  biData,
  menuItems,
  menuBiData,
  hasNewContent,
  betaBadge,
  icon,
  menuClassName,
  showTooltip = true,
  isTaller,
  onClick,
  id,
  'aria-label': ariaLabel,
  'aria-controls': ariaControls,
  'aria-expanded': ariaExpanded,
}) => {
  const { t } = useTranslation();

  const [isActionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const toggleDropdown = useCallback(() => setActionsDropdownOpen(isOpen => !isOpen), []);

  return (
    <ConditionalTooltip tooltipContent={tooltipContent} showTooltip={showTooltip && isCollapsed}>
      <div
        className={classnames(
          s['root'],
          isSelected && s['selected'],
          isCollapsed && s['collapsed'],
          isActionsDropdownOpen && s['actions-open'],
          menuItems && s['with-actions'],
          (url || onClick) && s['clickable'],
          isTaller && s['taller'],
          className
        )}
      >
        <PlainButtonOrLink
          id={id}
          href={url}
          className={classnames(s['item-link'], linkClassName)}
          tabIndex={tabIndex}
          biData={biData}
          onClick={onClick}
          aria-label={ariaLabel}
          aria-controls={ariaControls}
          aria-expanded={ariaExpanded}
        >
          <div className={classnames(s['icon'], iconClassName)}>
            {icon}
            {hasNewContent && <DotIndicator color="red" className={s['new-indicator']} />}
          </div>
          {!isCollapsed && children}
        </PlainButtonOrLink>
        {!isCollapsed && menuItems && (
          <IconButtonWithContextMenu
            icon={MoreIcon}
            size="medium"
            className={classnames(s['actions'], menuClassName)}
            standardMenuProps={{
              className: s['menu'],
              usePortal: true,
              popperClassName: 'networkbar-group-dropdown',
            }}
            popperManagerProps={{
              isInPopup: true,
            }}
            items={menuItems}
            onToggle={toggleDropdown}
            aria-label={t('networkBar.groups.actions.moreActions')}
            biData={isActionsDropdownOpen ? undefined : menuBiData}
          />
        )}
        {!isCollapsed && betaBadge && (
          <BetaBadge
            className={s['beta-badge']}
            badgeType={betaBadge}
            size="small"
            variant="green"
          />
        )}
      </div>
    </ConditionalTooltip>
  );
};
NetworkBarItem.displayName = 'NetworkBarItem';

export default NetworkBarItem;
